import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Trash2, CalendarClock, Send, Pause, Play, History, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import SchedulePicker, { defaultScheduleSpec } from '@/components/SchedulePicker';

// Schedule recurring PDF exports of dashboards/reports to email recipients.
export default function ExportScheduleDialog({ open, onOpenChange, resourceId, resourceName, resourceType = 'dashboard', onChanged }) {
  const [schedules, setSchedules] = useState([]);
  const [recipients, setRecipients] = useState('');
  const [format, setFormat] = useState('pdf');
  const [spec, setSpec] = useState(defaultScheduleSpec());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('new');
  // Digest: additional dashboards/reports bundled into one email.
  const [assets, setAssets] = useState([]);
  const [extraIds, setExtraIds] = useState([]);

  const load = async () => {
    setLoading(true);
    try { setSchedules(await api.listExportSchedules(resourceId)); } catch { /* ignore */ }
    try {
      const [dbs, reps] = await Promise.all([api.getDashboards().catch(() => []), api.getReports().catch(() => [])]);
      const norm = [
        ...(dbs || []).map(d => ({ id: d.id || d.dashboard_id || d._id, name: d.name || 'Dashboard', type: 'dashboard' })),
        ...(reps || []).map(r => ({ id: r.id || r.report_id || r._id, name: r.name || 'Report', type: 'report' })),
      ].filter(a => a.id && a.id !== resourceId);
      setAssets(norm);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { if (open) { setExtraIds([]); setSpec(defaultScheduleSpec()); setFormat('pdf'); load(); }   }, [open, resourceId]);

  const toggleExtra = (id) => setExtraIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const create = async () => {
    const list = recipients.split(/[,\n;]/).map(s => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient email'); return; }
    const resources = [
      { id: resourceId, name: resourceName, type: resourceType },
      ...assets.filter(a => extraIds.includes(a.id)),
    ];
    setSaving(true);
    try {
      const created = await api.createExportSchedule({
        dashboard_id: resourceId, dashboard_name: resourceName, resource_type: resourceType,
        resources, recipients: list, schedule: spec, timezone: spec.timezone,
        export_config: { format },
      });
      // "Also run once now" → fire the digest immediately after creating.
      if (spec.run_now && created?.schedule_id) {
        try { await api.sendTestExportSchedule(created.schedule_id); } catch { /* non-blocking */ }
      }
      toast.success(resources.length > 1 ? `Digest scheduled (${resources.length} items)` : 'Schedule created');
      setRecipients('');
      setExtraIds([]);
      await load();
    } catch (e) { toast.error(e.message || 'Could not create schedule'); }
    setSaving(false);
  };

  const remove = async (id) => {
    try { await api.deleteExportSchedule(id); await load(); onChanged?.(); toast.success('Schedule removed'); }
    catch (e) { toast.error(e.message || 'Could not remove'); }
  };

  const [testing, setTesting] = useState(null);
  const sendTest = async (id) => {
    setTesting(id);
    try {
      const r = await api.sendTestExportSchedule(id);
      toast.success(`Sample sent to ${(r.recipients || []).join(', ') || 'recipients'}`);
      await load();
      await loadHist(id);
      onChanged?.();
    } catch (e) { toast.error(e.message || 'Could not send sample email'); }
    setTesting(null);
  };

  // Per-schedule delivery history (success/failure + exact error per run).
  const [histOpen, setHistOpen] = useState(null);
  const [hist, setHist] = useState({});
  const [histErr, setHistErr] = useState({});
  const [histLoading, setHistLoading] = useState(null);
  const loadHist = async (id) => {
    setHistLoading(id);
    try {
      const d = await api.getExportScheduleDeliveries(id);
      setHist(h => ({ ...h, [id]: d || [] }));
      setHistErr(e => ({ ...e, [id]: null }));
    } catch (err) {
      setHistErr(e => ({ ...e, [id]: err.message || 'Could not load history' }));
    }
    setHistLoading(null);
  };
  const toggleHist = (id) => {
    if (histOpen === id) { setHistOpen(null); return; }
    setHistOpen(id);
    loadHist(id);  // always refresh on open so history is never stale
  };
  const deliveryWhen = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const [toggling, setToggling] = useState(null);
  const toggle = async (id) => {
    setToggling(id);
    try {
      const r = await api.toggleExportSchedule(id);
      toast.success(r.active ? 'Schedule resumed' : 'Schedule paused');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message || 'Could not update schedule'); }
    setToggling(null);
  };

  // Last-run status line: e.g. "Last sent Jun 3, 09:00 · Success" or a failure reason.
  const lastRunLabel = (s) => {
    if (!s.last_sent) return 'Not sent yet';
    let when;
    try { when = new Date(s.last_sent).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { when = s.last_sent; }
    const status = s.last_status === 'failed' ? 'Failed' : (s.last_status === 'success' ? 'Success' : '');
    return `Last sent ${when}${status ? ` · ${status}` : ''}`;
  };

  const scheduleLabel = (s) => {
    const count = (s.resources && s.resources.length) || 1;
    const suffix = count > 1 ? ` · ${count} items` : '';
    const recips = ` → ${s.recipients.join(', ')}`;
    if (s.schedule_label) return `${s.schedule_label}${suffix}${recips}`;
    const tz = s.timezone || 'UTC';
    return `${s.frequency} · ${String(s.hour).padStart(2, '0')}:00 ${tz}${suffix}${recips}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="export-schedule-dialog">
        <DialogHeader className="px-6 pt-6 pb-3"><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Schedule export</DialogTitle></DialogHeader>
        {/* Tabs consolidate the form and the (potentially long) active list so the dialog fits the viewport */}
        <div className="flex gap-1 px-6 pb-2">
          <button type="button" onClick={() => setTab('new')} className={`flex-1 h-8 rounded-md text-xs font-medium transition-colors ${tab === 'new' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="schedule-tab-new">New schedule</button>
          <button type="button" onClick={() => setTab('active')} className={`flex-1 h-8 rounded-md text-xs font-medium transition-colors ${tab === 'active' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="schedule-tab-active">Active{schedules.length ? ` (${schedules.length})` : ''}</button>
        </div>

        {tab === 'new' && (
        <>
        <div className="space-y-3 px-6 overflow-y-auto flex-1 min-h-0 pb-2">
          <div>
            <Label className="text-xs text-muted-foreground">Recipients (comma-separated)</Label>
            <Input value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="alex@acme.com, team@acme.com" className="mt-1 text-sm" data-testid="schedule-recipients" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Delivery format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="mt-1 text-sm" data-testid="schedule-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="xlsx">Excel</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="png">PNG (image)</SelectItem>
                <SelectItem value="pptx">PowerPoint</SelectItem>
                <SelectItem value="docx">Word</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="xml">XML</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Schedule</Label>
            <SchedulePicker value={spec} onChange={setSpec} />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Bundle more into one email (digest)</Label>
            <div className="mt-1 max-h-32 overflow-auto rounded-lg border border-border/40 p-2 space-y-1" data-testid="schedule-digest-list">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground/80">
                <input type="checkbox" checked disabled className="accent-primary" />
                <span className="text-foreground">{resourceName} <span className="capitalize opacity-60">({resourceType})</span></span>
              </div>
              {assets.length === 0 ? <p className="text-[10px] text-muted-foreground/70">No other dashboards or reports.</p>
                : assets.map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-[11px] cursor-pointer" data-testid={`schedule-digest-${a.id}`}>
                    <input type="checkbox" checked={extraIds.includes(a.id)} onChange={() => toggleExtra(a.id)} className="accent-primary" />
                    <span className="text-foreground">{a.name} <span className="capitalize opacity-60">({a.type})</span></span>
                  </label>
                ))}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground/70">Each email includes a rendered PDF of every selected item plus a view link.</p>
        </div>
        <div className="px-6 py-4 border-t border-border/40">
          <Button onClick={create} disabled={saving} className="w-full rounded-lg text-sm" data-testid="schedule-create-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1" />} Create schedule
          </Button>
        </div>
        </>
        )}

        {tab === 'active' && (
          <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              : schedules.length === 0 ? <p className="text-[11px] text-muted-foreground/80">No schedules yet. Use the “New schedule” tab to create one.</p>
              : (
                <div className="space-y-1.5" data-testid="schedule-list">
                  {schedules.map(s => (
                    <div key={s.schedule_id} className={`rounded-lg px-2.5 py-1.5 ${s.active === false ? 'bg-muted/10 opacity-60' : 'bg-muted/30'}`} data-testid={`schedule-row-${s.schedule_id}`}>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground flex items-center gap-1.5">
                          {s.active === false && <span className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 not-italic" data-testid={`schedule-paused-badge-${s.schedule_id}`}>Paused</span>}
                          {scheduleLabel(s)}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <button onClick={() => toggle(s.schedule_id)} disabled={toggling === s.schedule_id} className="text-muted-foreground hover:text-primary disabled:opacity-40" data-testid={`schedule-toggle-${s.schedule_id}`} title={s.active === false ? 'Resume schedule' : 'Pause schedule'}>
                            {toggling === s.schedule_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (s.active === false ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />)}
                          </button>
                          <button onClick={() => toggleHist(s.schedule_id)} className={`hover:text-primary ${histOpen === s.schedule_id ? 'text-primary' : 'text-muted-foreground'}`} data-testid={`schedule-history-${s.schedule_id}`} title="View delivery history">
                            <History className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => sendTest(s.schedule_id)} disabled={testing === s.schedule_id} className="text-muted-foreground hover:text-primary disabled:opacity-40 flex items-center gap-1" data-testid={`schedule-sendtest-${s.schedule_id}`} title="Send me a sample now">
                            {testing === s.schedule_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => remove(s.schedule_id)} className="text-muted-foreground hover:text-destructive" data-testid={`schedule-delete-${s.schedule_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1" data-testid={`schedule-laststatus-${s.schedule_id}`}>
                        <span className={`text-[10px] ${s.last_status === 'failed' ? 'text-rose-400' : 'text-muted-foreground/70'}`}>{lastRunLabel(s)}</span>
                        {s.last_error && <span className="text-[10px] text-amber-400/90" title={s.last_error}>· {s.last_error}</span>}
                      </div>
                      <button onClick={() => sendTest(s.schedule_id)} disabled={testing === s.schedule_id} className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary/90 hover:text-primary disabled:opacity-40" data-testid={`schedule-sample-btn-${s.schedule_id}`}>
                        {testing === s.schedule_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send me a sample now
                      </button>
                      {histOpen === s.schedule_id && (
                        <div className="mt-2 border-t border-border/30 pt-2" data-testid={`schedule-history-panel-${s.schedule_id}`}>
                          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-muted-foreground/60 mb-1"><ChevronDown className="w-3 h-3" /> Delivery history</div>
                          {histLoading === s.schedule_id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                            : histErr[s.schedule_id] ? <p className="text-[10px] text-rose-400">{histErr[s.schedule_id]}</p>
                            : !(hist[s.schedule_id] || []).length ? <p className="text-[10px] text-muted-foreground/70">No deliveries yet.</p>
                            : (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {(hist[s.schedule_id] || []).map(d => (
                                  <div key={d.log_id} className="flex items-start justify-between gap-2 text-[10px]" data-testid={`delivery-row-${d.log_id}`}>
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${d.status === 'failed' ? 'bg-rose-400' : 'bg-emerald-400'}`} />
                                      <span className="text-foreground/80">{deliveryWhen(d.run_at)}</span>
                                      <span className="text-[8px] uppercase tracking-wide px-1 rounded bg-muted/50 text-muted-foreground flex-shrink-0">{d.kind === 'test' ? 'sample' : 'scheduled'}</span>
                                    </div>
                                    <div className="text-right min-w-0">
                                      <span className={d.status === 'failed' ? 'text-rose-400' : 'text-muted-foreground/70'}>{d.status === 'failed' ? 'Failed' : `Sent ${d.sent}/${d.attempted}`}</span>
                                      {d.error && <div className="text-amber-400/90 truncate max-w-[220px]" title={d.error}>{d.error}</div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
